import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import { FlexContainer, Inputs, Spinner, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { routeStateSelector, searchSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import useValidateClabe from 'Hooks/useValidateClabe';
import {
  useRenewalFlex,
  useFetchFilingUser,
  usePatchFilingUser,
  usePostSubmitFiling,
  useSubmitDeclarationCrossSell,
} from 'Containers/Filing/hooks';
import { FilingComponentsProps } from 'Containers/Filing/types';
import { useGetUserCustomerData } from 'Containers/Datagathering/views/DgTypeForm/hooks';
import FilingSubmit from 'Containers/Filing/components/FilingSubmit';
import { useFetchPreviousYear } from 'Containers/Dashboard/hooks';
import { TaxReturnTypes } from 'Containers/Result/utils';
import { FRONT_PLANS, PLAN_FLEX_LITE, plansNameMapping } from 'Constants/plans';
import ClabeInput from './components/ClabeInput';
import * as Styled from './styles';
import {
  handleFlexPlan,
  handleFlexLitePlan,
  handleOtherPlans,
  calculateCompensationInfo,
} from './utils';

const DEFAULT_INSTALLMENTS = 1;

const ClabeForm = ({ onChangeStep, isAllowedStage }: FilingComponentsProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.FILING);
  const { mutate: autoRenewal } = useRenewalFlex();
  const planes = useSelector(searchSelector);
  const { plans } = queryString.parse(planes);
  const [clabeAccount, setClabeAccount] = useState('');
  const { isValid } = useValidateClabe(clabeAccount);
  const info = useSelector(routeStateSelector);
  const { mutate, isLoading } = usePatchFilingUser();
  const { mutate: submitDeclaration } = usePostSubmitFiling(onChangeStep);
  const { mutate: submitDeclarationCrossSell } = useSubmitDeclarationCrossSell();
  const { isLoading: loading, data: filingUserData } = useFetchFilingUser();
  const { data: previousYearData, isLoading: isLoadingPreviousYear } = useFetchPreviousYear();
  const customerInfo = useGetUserCustomerData();

  const plan = useMemo(
    () =>
      customerInfo && customerInfo.activePlan
        ? customerInfo.activePlan.name === PLAN_FLEX_LITE
          ? FRONT_PLANS.FLEX_LITE
          : plansNameMapping(customerInfo.activePlan.name)
        : FRONT_PLANS.BASIC,
    [customerInfo]
  );

  useEffect(() => {
    if (plans === FRONT_PLANS.FLEX) {
      autoRenewal({ isAutoRenewalEnabled: true });
    }
  }, []);

  const handleSubmit = (): void => {
    if (!isAllowedStage && clabeAccount) {
      handleFlexPlan(onChangeStep, mutate, clabeAccount, info?.compensation);
    } else if (!isAllowedStage && plan !== FRONT_PLANS.FLEX) {
      onChangeStep(7);
    } else if (plan === FRONT_PLANS.FLEX) {
      handleFlexPlan(onChangeStep, mutate, clabeAccount, info?.compensation, submitDeclaration);
    } else if (
      (plan === FRONT_PLANS.FLEX_LITE || plans === FRONT_PLANS.FLEX_LITE) &&
      !info?.isExtendedAndIsToReturn &&
      info?.taxReturnType !== TaxReturnTypes.TO_RETURN
    ) {
      handleFlexLitePlan(submitDeclarationCrossSell);
    } else {
      handleOtherPlans(
        info,
        clabeAccount,
        DEFAULT_INSTALLMENTS,
        mutate,
        submitDeclaration,
        filingUserData,
        info?.compensation
      );
    }
  };

  const { taxReturnType, taxReturnResult } = calculateCompensationInfo(previousYearData, info);

  return isLoading || loading || !filingUserData || isLoadingPreviousYear ? (
    <FlexContainer justify="center" align="center" fullWidth>
      <Spinner size="M" justify="center" align="center" />
    </FlexContainer>
  ) : (
    <FlexContainer
      direction="column"
      justify="space-between"
      align="center"
      gap="dimension_16"
      fullHeight
    >
      <Styled.ClabeContent direction="column" gap="dimension_16">
        <Text type="headline" size="XS_L_S" color="dark">
          {t(`PreFiling.clabe.title.${taxReturnType}`, {
            context: info?.fromFedUser && 'fedUser',
          })}
        </Text>
        <Text type="paragraphs" size="M_S_S" color="dark">
          {t(`PreFiling.clabe.subtitle.${taxReturnType}`, {
            context: info?.fromFedUser && 'fedUser',
          })}
        </Text>
        {taxReturnType === TaxReturnTypes.TO_PAY && (
          <Inputs.Text
            readOnly
            value={taxReturnResult.toString()}
            label={t(`PreFiling.clabe.input.${taxReturnType}`)}
          />
        )}
        {taxReturnType !== TaxReturnTypes.TO_PAY && (
          <ClabeInput
            setClabeAccount={setClabeAccount}
            clabeAccount={clabeAccount}
            data={filingUserData}
          />
        )}
      </Styled.ClabeContent>
      <FilingSubmit
        renderOmmitButton={!!clabeAccount}
        disabled={!isValid && taxReturnType === TaxReturnTypes.TO_RETURN}
        handleSubmit={handleSubmit}
        buttonLabel={`ClabeForm.CTA.${taxReturnType}`}
        returnType={taxReturnType}
        userPlan={plan}
      />
    </FlexContainer>
  );
};

export default ClabeForm;
